'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { Driver } from 'driver.js';

// 1. Hook for managing tutorial state in localStorage
// =================================================

interface TutorialState {
  currentStepId: string;
  currentStepIndex: number;
  isActive: boolean;
  timestamp: number;
}

const TUTORIAL_STORAGE_KEY = 'workflow_tutorial_state';

export function useTutorialState() {
  const saveState = useCallback((stepId: string, stepIndex: number) => {
    try {
      if (stepIndex > 0 && stepIndex <= 4) {
        // Don't save state before step 4 execution, since the form resets
        return;
      }
      const state: TutorialState = {
        currentStepId: stepId,
        currentStepIndex: stepIndex,
        isActive: true,
        timestamp: Date.now(),
      };
      localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save tutorial state:', error);
    }
  }, []);

  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear tutorial state:', error);
    }
  }, []);

  const getState = useCallback((): TutorialState | null => {
    try {
      const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
      if (!stored) return null;

      const state = JSON.parse(stored) as TutorialState;
      // Clear state if it's older than 24 hours to prevent stale states
      if (Date.now() - state.timestamp > 24 * 60 * 60 * 1000) {
        console.log('Clearing stale tutorial state:', state);
        clearState();
        return null;
      }

      return state;
    } catch (error) {
      console.error('Failed to get tutorial state:', error);
      return null;
    }
  }, [clearState]);

  return { saveState, clearState, getState };
}

// 2. Hook for creating tutorial steps
// ===================================

export type TutorialStepSide = 'top' | 'right' | 'bottom' | 'left';

export interface TutorialStep {
  id: string;
  /** CSS selector of the element to highlight. Omitted for a centered step. */
  element?: string;
  side?: TutorialStepSide;
  popoverClass?: string;
  title: string;
  text: string;
  /**
   * Steps that show their own in-tour "Next" button instead of only
   * advancing when the app calls advanceTutorial() after an on-page action.
   * Defaults to driver.moveNext() when no onClick is given.
   */
  nextButton?: {
    label: string;
    onClick?: (driver: Driver) => void;
  };
  /** The final step's "Finish Tour" button. */
  doneButton?: {
    label: string;
    onClick: (driver: Driver) => void;
  };
}

export function useTutorialSteps(): TutorialStep[] {
  const t = useTranslations('Tutorial');

  return [
    {
      id: 'paste-job-description',
      element: '[data-tutorial="job-description-input"]',
      side: 'left',
      title: t('step1.title'),
      text: t('step1.text'),
    },
    {
      id: 'select-tone',
      element: '[data-tutorial="tone-input"]',
      side: 'left',
      title: t('step2.title'),
      text: t('step2.text'),
    },
    {
      id: 'fill-notes',
      element: '[data-tutorial="notes-input"]',
      side: 'left',
      title: t('step3.title'),
      text: t('step3.text'),
    },
    {
      id: 'select-language',
      element: '[data-tutorial="language-select"]',
      side: 'left',
      title: t('step4.title'),
      text: t('step4.text'),
      popoverClass: 'mt-4',
    },
    {
      id: 'select-output-length',
      element: '[data-tutorial="length-select"]',
      side: 'left',
      title: t('step5.title'),
      text: t('step5.text'),
    },
    {
      id: 'credits-deducted',
      element: '[data-tutorial="credits-display"]',
      side: 'right',
      title: t('step7.title'),
      text: t('step7.text'),
      nextButton: {
        label: t('nextStep'),
        onClick(driver) {
          const hasWorkflowOutput = document.querySelector('[data-tutorial="create-job-button"]');
          if (hasWorkflowOutput) {
            // Outputs exist, skip to "create-job-from-output" step
            driver.moveNext();
          }
          driver.moveNext();
        },
      },
    },
    {
      id: 'workflow-processing',
      element: '[data-tutorial="workflow-loading"]',
      side: 'bottom',
      title: t('step8.title'),
      text: t('step8.text'),
    },
    {
      id: 'create-job-from-output',
      element: '[data-tutorial="create-job-button"]',
      side: 'left',
      title: t('step9.title'),
      text: t('step9.text'),
    },
    {
      id: 'fill-job-details',
      element: '[data-tutorial="job-title-input"]',
      side: 'left',
      title: t('step10.title'),
      text: t('step10.text'),
    },
    {
      id: 'job-details-overview',
      element: '[data-tutorial="job-details"]',
      side: 'bottom',
      title: t('step11.title'),
      text: t('step11.text'),
      nextButton: {
        label: t('nextStep'),
      },
    },
    {
      id: 'reusable-inputs',
      element: '[data-tutorial="reusable-inputs"]',
      side: 'left',
      title: t('step12.title'),
      text: t('step12.text'),
      nextButton: {
        label: t('nextStep'),
      },
    },
    {
      id: 'activate-job',
      element: '[data-tutorial="job-selector"]',
      side: 'right',
      title: t('step13.title'),
      text: t('step13.text'),
      nextButton: {
        label: t('nextStep'),
      },
    },
    {
      id: 'job-products',
      element: '[data-tutorial="job-products"]',
      side: 'bottom',
      title: t('step14.title'),
      text: t('step14.text'),
      nextButton: {
        label: t('nextStep'),
      },
    },
    {
      id: 'tutorial-complete',
      title: t('step15.title'),
      text: t('step15.text'),
      doneButton: {
        label: t('finishTour'),
        onClick(driver) {
          try {
            console.log('Completing tutorial from final step');
            driver.destroy();
          } catch (error) {
            console.error('Failed to complete tutorial:', error);
          }
        },
      },
    },
  ];
}
